/* ============================================
   Separación por dominio
   - Admin: erp.1conecta.com
   - Clientes y partners: client.1conecta.com (y portal.1conecta.com)
   Un rol que cae en el dominio equivocado es redirigido al correcto.
   Los dominios se pueden sobreescribir por env (VITE_ADMIN_URL / VITE_PORTAL_URL).
   ============================================ */

const ADMIN_URL = import.meta.env.VITE_ADMIN_URL || 'https://erp.1conecta.com'
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://client.1conecta.com'

export type HostKind = 'admin' | 'portal' | 'dev'

/** Clasifica el host actual. 'dev' = localhost/IP/preview (sin enforcement). */
export function getHostKind(): HostKind {
  const h = window.location.hostname
  if (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    /^\d+\.\d+\.\d+\.\d+$/.test(h) ||
    h.endsWith('.pages.dev')
  ) return 'dev'
  if (h.startsWith('client.') || h.startsWith('portal.')) return 'portal'
  return 'admin'
}

type RoleLike = 'admin' | 'ventas' | 'cliente' | 'partner' | null

/**
 * ¿Este rol vive en el dominio del ERP? `ventas` es gente nuestra: entra por
 * `erp.1conecta.com` igual que un admin, aunque vea menos.
 *
 * Es una función y no una comparación suelta porque la respuesta se necesita
 * en tres lados (acá, el login y el guard) y las tres tienen que coincidir: si
 * una dijera que no, la sesión nacería en el origen equivocado y, como el
 * token vive en `localStorage` —que es por origen—, se perdería al cruzar.
 */
export function esPanelAdmin(role: RoleLike): boolean {
  return role === 'admin' || role === 'ventas'
}

/**
 * El correo viaja entre dominios en el FRAGMENTO (`#correo=`) y no en la query:
 * el fragmento no se manda en la peticion HTTP, asi que no queda en el log de
 * ningun servidor ni en la cabecera `Referer`. El login lo lee y lo borra de la
 * barra en el acto.
 */
export const CLAVE_HANDOFF = 'correo'

/** `<dominio>/login`, con el correo en el fragmento si se conoce. */
export function urlDeLogin(dominio: string, correo?: string | null): string {
  return correo
    ? `${dominio}/login#${CLAVE_HANDOFF}=${encodeURIComponent(correo)}`
    : `${dominio}/login`
}

/**
 * Si el rol no corresponde al dominio actual, devuelve la URL absoluta del
 * dominio correcto. Si el dominio ya es el correcto (o es dev), null.
 * role null = identidad autenticada pendiente de elegir cuenta → se trata como
 * no-admin.
 *
 * Manda al LOGIN del otro dominio y no a la misma ruta, aunque la ruta se
 * pudiera conservar: la sesion vive en `localStorage`, que es por origen, asi
 * que al cruzar se pierde siempre. Conservando la ruta, el usuario aterrizaba
 * en una pantalla protegida sin token, el guard lo mandaba al login y el
 * resultado era un parpadeo y un formulario vacio. Con el correo en el
 * fragmento, aterriza en el paso de la contrasena.
 */
export function crossDomainTarget(role: RoleLike, correo?: string | null): string | null {
  const kind = getHostKind()
  if (kind === 'dev') return null
  if (esPanelAdmin(role) && kind === 'portal') return urlDeLogin(ADMIN_URL, correo)
  if (!esPanelAdmin(role) && kind === 'admin') return urlDeLogin(PORTAL_URL, correo)
  return null
}

export type Panel = 'admin' | 'portal'

/**
 * El dominio donde vive un panel, si NO es este. `null` cuando ya estamos en el
 * correcto, o en dev (localhost / preview), donde los dos paneles conviven.
 *
 * Es el gemelo de `crossDomainTarget` para el login: aquel resuelve una sesion
 * ya abierta en el dominio equivocado —y al cruzar la pierde, porque el token
 * vive en `localStorage`, que es por origen—, y este resuelve ANTES de que la
 * sesion exista, que es la unica forma de que nazca en el origen correcto.
 */
export function dominioDelPanel(panel: Panel): string | null {
  const kind = getHostKind()
  if (kind === 'dev' || kind === panel) return null
  return panel === 'admin' ? ADMIN_URL : PORTAL_URL
}
